import { useState, useEffect } from 'react';
import { X, Calendar, Package, DollarSign, Loader2, AlertCircle } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { toast } from 'sonner';
import { useFormatters } from '../../contexts/PreferencesContext';
import { ReasonCodeSelect, getEffectiveReason, type WorkflowModalProps } from './AcknowledgmentSharedUtils';

// Extended interface for Propose Change modal (supports multiple change types)
interface ProposeChangeModalProps extends WorkflowModalProps {
  currentQuantity: string;
  currentUnitPrice: string;
  userType?: 'supplier' | 'customer';
}

// CR-3401: Comprehensive Propose Change Modal - supports multiple simultaneous changes
export function ProposeChangeModal({
  isOpen,
  onClose,
  lineId,
  lineNumber,
  partNumber,
  orderNumber,
  currentDeliveryDate,
  currentQuantity,
  currentUnitPrice,
  onSubmit,
  userType,
}: ProposeChangeModalProps) {
  const { formatCurrency, formatDate } = useFormatters();
  // CR-3401: Changed to checkboxes for multiple selections
  const [changeDate, setChangeDate] = useState(false);
  const [changeQuantity, setChangeQuantity] = useState(false);
  const [changePrice, setChangePrice] = useState(false);
  const [proposedDate, setProposedDate] = useState('');
  const [proposedQuantity, setProposedQuantity] = useState('');
  const [proposedPrice, setProposedPrice] = useState('');
  // Per-field reason codes
  const [dateReasonSelect, setDateReasonSelect] = useState('');
  const [dateCustomReason, setDateCustomReason] = useState('');
  const [qtyReasonSelect, setQtyReasonSelect] = useState('');
  const [qtyCustomReason, setQtyCustomReason] = useState('');
  const [priceReasonSelect, setPriceReasonSelect] = useState('');
  const [priceCustomReason, setPriceCustomReason] = useState('');
  const [comments, setComments] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const hasAnyChange = changeDate || changeQuantity || changePrice;

  // CR-4866: Seed proposed date with the existing promise date when modal opens,
  // so the date picker defaults to the current value rather than today.
  useEffect(() => {
    if (!isOpen || !currentDeliveryDate) return;
    const d = new Date(currentDeliveryDate);
    if (Number.isNaN(d.getTime())) return;
    setProposedDate(d.toISOString().split('T')[0]);
  }, [isOpen, currentDeliveryDate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation - at least one change type must be selected
    if (!hasAnyChange) {
      toast.error('Please select at least one type of change');
      return;
    }

    // CR-3401: Validate each selected change type
    if (changeDate && !proposedDate) {
      toast.error('Please select a proposed delivery date');
      return;
    }
    if (changeQuantity && !proposedQuantity) {
      toast.error('Please enter a proposed quantity');
      return;
    }
    if (changePrice && !proposedPrice) {
      toast.error('Please enter a proposed price');
      return;
    }

    // Validate each selected field has a reason code
    if (changeDate && !getEffectiveReason(dateReasonSelect, dateCustomReason)) {
      toast.error('Please select a reason code for the Promise Date change');
      return;
    }
    if (changeQuantity && !getEffectiveReason(qtyReasonSelect, qtyCustomReason)) {
      toast.error('Please select a reason code for the Quantity change');
      return;
    }
    if (changePrice && !getEffectiveReason(priceReasonSelect, priceCustomReason)) {
      toast.error('Please select a reason code for the Unit Price change');
      return;
    }

    setSubmitting(true);
    try {
      // CR-3401: Submit all changes together with per-field reason codes
      const changes: Array<{ action: string; lineId: number; comments?: string; reason?: string; proposedDate?: string; proposedQuantity?: number; proposedPrice?: number }> = [];

      if (changeDate) {
        changes.push({
          lineId,
          action: 'propose',
          proposedDate,
          reason: getEffectiveReason(dateReasonSelect, dateCustomReason),
          comments,
        });
      }
      if (changeQuantity) {
        changes.push({
          lineId,
          action: 'proposeQuantity',
          proposedQuantity: parseFloat(proposedQuantity) || 0,
          reason: getEffectiveReason(qtyReasonSelect, qtyCustomReason),
          comments,
        });
      }
      if (changePrice) {
        changes.push({
          lineId,
          action: 'proposePrice',
          proposedPrice: parseFloat(proposedPrice) || 0,
          reason: getEffectiveReason(priceReasonSelect, priceCustomReason),
          comments,
        });
      }

      // Submit all changes sequentially
      for (const change of changes) {
        await onSubmit(change);
      }

      // Reset form
      setProposedDate('');
      setProposedQuantity('');
      setProposedPrice('');
      setDateReasonSelect('');
      setDateCustomReason('');
      setQtyReasonSelect('');
      setQtyCustomReason('');
      setPriceReasonSelect('');
      setPriceCustomReason('');
      setComments('');
      setChangeDate(false);
      setChangeQuantity(false);
      setChangePrice(false);
      onClose();

      if (changes.length > 1) {
        toast.success(`Successfully proposed ${changes.length} changes`);
      }
    } catch (error) {
      console.error('Failed to propose change:', error);
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <Card className="w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-orange-600" />
            <h3 className="text-lg font-semibold">Propose Change</h3>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-muted rounded-full transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <CardContent className="p-4">
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Line Info */}
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 space-y-2">
              {orderNumber && (
                <div className="flex justify-between text-sm">
                  <span className="font-medium">Order Number:</span>
                  <span>{orderNumber}</span>
                </div>
              )}
              <div className="flex justify-between text-sm">
                <span className="font-medium">Line:</span>
                <span>{lineNumber}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="font-medium">Part:</span>
                <span className="font-mono">{partNumber}</span>
              </div>
            </div>

            {/* CR-3401: Change Type Selection with Checkboxes */}
            <div>
              <label className="block text-sm font-medium mb-2">
                What would you like to change? <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-3 gap-2">
                <button
                  type="button"
                  onClick={() => setChangeDate(!changeDate)}
                  className={`p-3 border rounded-lg flex flex-col items-center gap-2 transition-colors ${
                    changeDate
                      ? 'border-orange-500 bg-orange-50 text-orange-700'
                      : 'border-border hover:bg-muted'
                  }`}
                >
                  <div className="flex items-center gap-1">
                    <input
                      type="checkbox"
                      checked={changeDate}
                      onChange={() => setChangeDate(!changeDate)}
                      className="w-4 h-4 accent-orange-600"
                      onClick={(e) => e.stopPropagation()}
                    />
                    <Calendar className="h-5 w-5" />
                  </div>
                  <span className="text-xs font-medium">Customer Promise Date</span>
                </button>
                {/* CR-4722: Quantity and Price buttons hidden for supplier users */}
                {userType !== 'supplier' && (
                <button
                  type="button"
                  onClick={() => setChangeQuantity(!changeQuantity)}
                  className={`p-3 border rounded-lg flex flex-col items-center gap-2 transition-colors ${
                    changeQuantity
                      ? 'border-orange-500 bg-orange-50 text-orange-700'
                      : 'border-border hover:bg-muted'
                  }`}
                >
                  <div className="flex items-center gap-1">
                    <input
                      type="checkbox"
                      checked={changeQuantity}
                      onChange={() => setChangeQuantity(!changeQuantity)}
                      className="w-4 h-4 accent-orange-600"
                      onClick={(e) => e.stopPropagation()}
                    />
                    <Package className="h-5 w-5" />
                  </div>
                  <span className="text-xs font-medium">Quantity</span>
                </button>
                )}
                {userType !== 'supplier' && (
                <button
                  type="button"
                  onClick={() => setChangePrice(!changePrice)}
                  className={`p-3 border rounded-lg flex flex-col items-center gap-2 transition-colors ${
                    changePrice
                      ? 'border-orange-500 bg-orange-50 text-orange-700'
                      : 'border-border hover:bg-muted'
                  }`}
                >
                  <div className="flex items-center gap-1">
                    <input
                      type="checkbox"
                      checked={changePrice}
                      onChange={() => setChangePrice(!changePrice)}
                      className="w-4 h-4 accent-orange-600"
                      onClick={(e) => e.stopPropagation()}
                    />
                    <DollarSign className="h-5 w-5" />
                  </div>
                  <span className="text-xs font-medium">Unit Price</span>
                </button>
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-1">Select one or more change types</p>
            </div>

            {/* CR-3401: Delivery Date Change Section */}
            {changeDate && (
              <div className="border border-orange-200 rounded-lg p-3 bg-orange-50/50 space-y-2">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-orange-600" />
                  <span className="text-sm font-medium text-orange-700">Promise Date Change</span>
                </div>
                <div className="bg-white rounded p-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Current:</span>
                    <span>{formatDate(currentDeliveryDate)}</span>
                  </div>
                </div>
                <input
                  type="date"
                  value={proposedDate}
                  onChange={(e) => setProposedDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-white"
                />
                <ReasonCodeSelect
                  value={dateReasonSelect}
                  onChange={setDateReasonSelect}
                  customValue={dateCustomReason}
                  onCustomChange={setDateCustomReason}
                  required
                  label="Promise Date Reason Code"
                />
              </div>
            )}

            {/* CR-3401: Quantity Change Section */}
            {changeQuantity && (
              <div className="border border-orange-200 rounded-lg p-3 bg-orange-50/50 space-y-2">
                <div className="flex items-center gap-2">
                  <Package className="h-4 w-4 text-orange-600" />
                  <span className="text-sm font-medium text-orange-700">Quantity Change</span>
                </div>
                <div className="bg-white rounded p-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Current:</span>
                    <span>{parseFloat(currentQuantity).toFixed(0)}</span>
                  </div>
                </div>
                <input
                  type="number"
                  value={proposedQuantity}
                  onChange={(e) => setProposedQuantity(e.target.value)}
                  min="1"
                  step="1"
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-white"
                  placeholder="Enter proposed quantity"
                />
                <ReasonCodeSelect
                  value={qtyReasonSelect}
                  onChange={setQtyReasonSelect}
                  customValue={qtyCustomReason}
                  onCustomChange={setQtyCustomReason}
                  required
                  label="Quantity Reason Code"
                />
              </div>
            )}

            {/* CR-3401: Price Change Section */}
            {changePrice && (
              <div className="border border-orange-200 rounded-lg p-3 bg-orange-50/50 space-y-2">
                <div className="flex items-center gap-2">
                  <DollarSign className="h-4 w-4 text-orange-600" />
                  <span className="text-sm font-medium text-orange-700">Unit Price Change</span>
                </div>
                <div className="bg-white rounded p-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Current:</span>
                    <span>{formatCurrency(parseFloat(currentUnitPrice))}</span>
                  </div>
                </div>
                <input
                  type="number"
                  value={proposedPrice}
                  onChange={(e) => setProposedPrice(e.target.value)}
                  min="0"
                  step="0.01"
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary bg-white"
                  placeholder="Enter proposed price (GBP)"
                />
                <ReasonCodeSelect
                  value={priceReasonSelect}
                  onChange={setPriceReasonSelect}
                  customValue={priceCustomReason}
                  onCustomChange={setPriceCustomReason}
                  required
                  label="Unit Price Reason Code"
                />
              </div>
            )}

            {/* Optional additional comments */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Additional Comments
              </label>
              <textarea
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                rows={3}
                placeholder="Provide additional details about the proposed change..."
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                className="px-4 py-2 border rounded-lg hover:bg-muted disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting || !hasAnyChange}
                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 flex items-center gap-2"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Proposing...
                  </>
                ) : (
                  <>
                    <AlertCircle className="h-4 w-4" />
                    {/* CR-3401: Show count of changes being proposed */}
                    Propose {[changeDate, changeQuantity, changePrice].filter(Boolean).length} Change{[changeDate, changeQuantity, changePrice].filter(Boolean).length !== 1 ? 's' : ''}
                  </>
                )}
              </button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
